import React, { useEffect } from 'react';
import { Loader2, CheckCircle, Plus, Eye } from 'lucide-react';
import { Modal, Button } from '../ui';
import { CaseFormEditor } from './CaseFormEditor';
import { ValidationErrors } from './ValidationErrors';
import { useCaseEntry } from '../../hooks/useCaseEntry';
import { validateCase } from '../../services/caseValidationService';

import { useAppStore } from '../../context/store';

interface CaseEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCaseAdded?: (caseId: string) => void;
}

export const CaseEntryModal: React.FC<CaseEntryModalProps> = ({
  isOpen,
  onClose,
  onCaseAdded,
}) => {
  const {
    step,
    confidenceScores,
    editedData,
    validationErrors,
    isLoading,
    error,
    savedCase,
    extractFromUrl,
    updateField,
    save,
    reset,
  } = useCaseEntry();

  const { addToComparison } = useAppStore();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    const result = await save();
    if (result) {
      addToComparison(result.id);
      onCaseAdded?.(result.id);
    }
  };

  const handleAddAnother = () => {
    reset();
  };

  const handleViewCase = () => {
    if (savedCase) {
      handleClose();
      onCaseAdded?.(savedCase.id);
    }
  };

  // Convert validation errors array to record for form
  const errorRecord: Record<string, string> = {};
  validationErrors.forEach((err) => {
    errorRecord[err.field] = err.message;
  });

  // Get validation result for warnings and duplicate detection
  const validationResult = validateCase(editedData);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Case"
      size="full"
    >
      {/* Form */}
      {step === 'form' && (
        <div className="space-y-6">
          {/* Validation errors */}
          <ValidationErrors
            errors={validationErrors}
            warnings={validationResult.warnings}
            duplicateCase={validationResult.duplicateCase}
          />

          {/* Form */}
          <CaseFormEditor
            data={editedData}
            confidenceScores={confidenceScores}
            errors={errorRecord}
            onChange={updateField}
            onExtract={extractFromUrl}
            isExtracting={isLoading}
            showImageSection={false}
            showReferralLinks={false}
          />

          {error && (
            <div className="p-3 bg-danger-soft border border-danger/40 rounded-xl text-sm text-danger">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-line">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              Save Case
            </Button>
          </div>
        </div>
      )}

      {/* Extracting (Loading State) */}
      {step === 'extracting' && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 size={48} className="text-accent animate-spin mb-4" />
          <p className="text-lg font-medium text-fg mb-2">
            Extracting specifications...
          </p>
          <p className="text-sm text-muted">
            Analyzing the page content with AI
          </p>
        </div>
      )}

      {/* Success */}
      {step === 'success' && savedCase && (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 rounded-full bg-accent-soft flex items-center justify-center mb-4">
            <CheckCircle size={32} className="text-accent-ink" />
          </div>
          <h3 className="text-xl font-semibold text-fg mb-2">
            Case Added Successfully!
          </h3>
          <p className="text-muted mb-6 text-center">
            <span className="font-medium">{savedCase.name}</span> by{' '}
            {savedCase.manufacturer} has been added to your collection.
          </p>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              leftIcon={<Plus size={16} />}
              onClick={handleAddAnother}
            >
              Add Another
            </Button>
            <Button
              variant="outline"
              leftIcon={<Eye size={16} />}
              onClick={handleViewCase}
            >
              View Case
            </Button>
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
